import React, { useState, useContext, useEffect } from "react";
import { message, Input, List, Tag, Modal } from "antd";
import { CopyOutlined, FilePdfOutlined, SearchOutlined } from "@ant-design/icons";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import orderBy from "lodash/orderBy";
import {
  GET_COMPANY_DOCUMENTS,
  GET_LEAD_OPPORTUNITY_DOCUMENT_URL,
  GET_LEAD_OPPORTUNITY_DOCUMENTS,
} from "../../../graphql/query";
import { SEND_DOCUMENT_FOR_SIGNATURE } from "../../../graphql/mutation";
import { closeDialog } from "../../../library/helpers";
import DialogFooter from "../../commonComponents/dialogFooter";
import { getHighlightedText } from "../../../library/utils";
import CustomButton from "../../commonComponents/button";
import OpportunityFilter from "./opporotunityLayer/opportunityFilter";
import CustomLabel from "../../commonComponents/label";
import { UserContext } from "../../../amplify/authenticator-provider";
import useLang from "../../../hooks/useLang";

let selectedId;

// Maps a SignatureStatusType value to an antd Tag colour.
const SIGNATURE_STATUS_COLOR = {
  DRAFT: "default",
  SENT: "processing",
  VIEWED: "blue",
  SIGNED: "success",
  DECLINED: "error",
  CANCELLED: "warning",
  EXPIRED: "warning",
};

const copyToClipboard = async value => {
  try {
    await navigator.clipboard.writeText(value);
    message.success("Link copied");
  } catch (e) {
    message.error("Could not copy — select and copy manually");
  }
};

export default function DocumentSelector(props) {
  const [lang] = useLang();
  const { user } = useContext(UserContext);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50 });
  const [search, setSearch] = useState("");
  const [opportunity, setOpportunity] = useState();
  const opprList = (props?.opportunityList || []).filter(el => el.name !== "All");
  const opportunities = orderBy(opprList, item => new Date(item.updatedDate), "desc");

  useEffect(() => {
    if (props?.activeOppName && !opportunity) {
      setOpportunity(
        props?.activeOppName?.name === "All" && opportunities.length > 0
          ? opportunities[0]?.id
          : props?.activeOppName?.id
      );
    } else if (opportunities.length > 0) {
      setOpportunity(opportunities[0]?.id);
    }
  }, [opportunity, props?.activeOppName, opportunities]);

  const { data: documentData, loading: docLoader } = useQuery(GET_COMPANY_DOCUMENTS, {
    fetchPolicy: "cache-and-network",
    variables: {
      companyId: user?.company.id,
      locationId: sessionStorage.getItem("currentLocationId"),
      search: search,
      page: 1,
      pageSize: 25,
    },
  });

  const [getDocumentLink, { loading: docLinkLoading }] = useLazyQuery(
    GET_LEAD_OPPORTUNITY_DOCUMENT_URL,
    {
      fetchPolicy: "network-only",
      onCompleted: resp => {
        if (resp?.getLeadOpportunityDocumentUrl?.statusCode === 200) {
          const documentUrl = resp?.getLeadOpportunityDocumentUrl?.documentUrl;
          if (documentUrl) {
            window.open(documentUrl, "_blank").focus();
            selectedId = null;
          }
        } else {
          message.error(resp?.getLeadOpportunityDocumentUrl?.statusMessage);
        }
      },
    }
  );
  // Tracks the row currently being sent + the resulting status per document id.
  const [sendingId, setSendingId] = useState(null);
  const [signatureStateById, setSignatureStateById] = useState({});

  // Previously generated/sent documents for this lead+opportunity.
  const { data: generatedDocsData, refetch: refetchGeneratedDocs } = useQuery(
    GET_LEAD_OPPORTUNITY_DOCUMENTS,
    {
      fetchPolicy: "cache-and-network",
      skip: !props?.leadId,
      variables: {
        leadId: props?.leadId,
        leadOpportunityId: opportunity,
      },
    }
  );
  const generatedDocs = generatedDocsData?.getLeadOpportunityDocuments || [];

  // Show the customer-shareable short link so the salesperson can copy + send it.
  const showShareLink = shareLink => {
    Modal.success({
      title: "Document ready to share",
      content: (
        <div className="flex flex-col gap-2">
          <span className="text-sm">
            Share this link with the customer to view/download the document:
          </span>
          <Input readOnly value={shareLink} onFocus={e => e.target.select()} />
        </div>
      ),
      okText: "Copy link",
      onOk: () => copyToClipboard(shareLink),
    });
  };

  const [sendForSignature, { loading: sendLoading }] = useMutation(SEND_DOCUMENT_FOR_SIGNATURE, {
    fetchPolicy: "network-only",
    onCompleted: resp => {
      const result = resp?.sendDocumentForSignature;
      if (result?.statusCode === 200) {
        setSignatureStateById(prev => ({
          ...prev,
          [sendingId]: result.signatureStatus,
        }));
        message.success(result?.statusMessage || "Document generated");
        if (result?.shareLink) {
          showShareLink(result.shareLink);
        }
        if (refetchGeneratedDocs) {
          refetchGeneratedDocs();
        }
      } else {
        message.error(result?.statusMessage || "Failed to send for signature");
      }
      setSendingId(null);
    },
    onError: () => {
      message.error("Failed to send document for signature");
      setSendingId(null);
    },
  });

  const count = documentData?.getCompanyDocuments?.companyDocuments?.length || 0;

  return (
    <div>
      <CustomLabel label={lang.selectOpportunity} labelClass="m-0" />
      <OpportunityFilter
        className="w-full my-2"
        oppValue={opportunity}
        disabled={!props?.leadId}
        handleFilter={setOpportunity}
        activeOppName={props?.activeOppName || ""}
        opportunityList={opportunities || []}
      />
      <CustomLabel label="Documents" labelClass="m-0" />
      <Input
        className="w-full"
        placeholder="Search documents"
        prefix={<SearchOutlined />}
        value={search}
        onChange={e => setSearch(e.target.value)}
      />
      <List
        loading={docLoader}
        className="mt-2 max-h-[300px] overflow-y-auto"
        dataSource={documentData?.getCompanyDocuments?.companyDocuments || []}
        renderItem={(item, index) => {
          return (
            <div key={index} className="flex flex-row p-2 gap-3 mr-4 mb-4 cursor-pointer">
              <FilePdfOutlined className="text-[20px]" />
              <div className="flex flex-col gap-3 ml-2 w-full">
                <div className="flex flex-col gap-1 w-full">
                  <div className="flex flex-row items-center justify-between w-full">
                    <span className="text-sm flex flex-row items-center gap-2">
                      {getHighlightedText(item?.name, search)}
                      {signatureStateById[item.id] ? (
                        <Tag
                          color={SIGNATURE_STATUS_COLOR[signatureStateById[item.id]] || "default"}
                        >
                          {signatureStateById[item.id]}
                        </Tag>
                      ) : null}
                    </span>
                    <div className="flex flex-row items-center gap-2">
                      <CustomButton
                        btnText="Print"
                        key="print"
                        loading={item.id === selectedId && docLinkLoading}
                        type="default"
                        handleClick={async () => {
                          selectedId = item.id;
                          await getDocumentLink({
                            variables: {
                              companyDocumentId: item.id,
                              leadId: props?.leadId,
                              leadOpportunityId: opportunity,
                            },
                          });
                        }}
                      />
                      <CustomButton
                        btnText="Send for signature"
                        key="send"
                        disabled={!props?.leadId}
                        loading={item.id === sendingId && sendLoading}
                        type="primary"
                        handleClick={async () => {
                          setSendingId(item.id);
                          await sendForSignature({
                            variables: {
                              companyDocumentId: item.id,
                              leadId: props?.leadId,
                              leadOpportunityId: opportunity,
                            },
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        }}
        pagination={
          count < 5
            ? null
            : {
                current: pagination.page,
                pageSize: pagination.pageSize,
                total: count || 0,
                showTotal: total => `${lang.total}: ${total} ${lang.vehicles}`,
                pageSizeOptions: ["25", "50", "100", "150", "200", "500"],
                showSizeChanger: true,
                onChange: (page, pageSize) => {
                  setPagination({ page, pageSize });
                },
                onShowSizeChange: (current, size) => {
                  setPagination({ page: current, pageSize: size });
                },
              }
        }
        itemLayout="horizontal"
      />

      {generatedDocs.length > 0 ? (
        <div className="mt-3">
          <CustomLabel label="Generated documents" labelClass="m-0" />
          <List
            className="mt-1 max-h-[200px] overflow-y-auto"
            dataSource={generatedDocs}
            renderItem={doc => (
              <div className="flex flex-row items-center justify-between p-2 gap-2">
                <span className="text-sm flex flex-row items-center gap-2">
                  <FilePdfOutlined />
                  {doc?.name}
                  <span className="text-gray-400">v{doc?.documentVersion}</span>
                  {doc?.signatureStatus && doc?.signatureStatus !== "NONE" ? (
                    <Tag color={SIGNATURE_STATUS_COLOR[doc.signatureStatus] || "default"}>
                      {doc.signatureStatus}
                    </Tag>
                  ) : null}
                </span>
                <div className="flex flex-row items-center gap-3">
                  <CustomButton
                    btnText="View"
                    key="view"
                    type="default"
                    handleClick={() => {
                      if (doc?.documentUrl) {
                        window.open(doc.documentUrl, "_blank").focus();
                      }
                    }}
                  />
                  <CopyOutlined
                    className="cursor-pointer"
                    title="Copy document link"
                    onClick={() => copyToClipboard(doc?.documentUrl)}
                  />
                </div>
              </div>
            )}
            itemLayout="horizontal"
          />
        </div>
      ) : null}

      <DialogFooter
        loading={docLoader || docLinkLoading || sendLoading}
        showConfirmButton={false}
        confirmBtnText=""
        handleCancel={() => closeDialog("documentSelector")}
        align="left"
      />
    </div>
  );
}
